import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardService, DashboardSummary } from '@/app/services/dashboardService';
import DailySalesWidget from './components/DailySalesWidget';
import DailyPaymentsWidget from './components/DailyPaymentsWidget';
import QuickStatsCard from './components/QuickStatsCard';
import UpcomingTasksWidget from './components/UpcomingTasksWidget';
import { HandCoins, Banknote, RefreshCw } from 'lucide-react';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format, endOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';

const ALL_TIME_START = new Date(2020, 0, 1);

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(amount);
};

const DashboardPage: React.FC = () => {
    const queryClient = useQueryClient();
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: ALL_TIME_START,
        to: endOfDay(new Date())
    });

    useEffect(() => {
        document.title = 'Dashboard - Clara Builders';
        return () => {
            document.title = 'Clara Builders';
        };
    }, []);

    const { data: summary, isLoading: summaryLoading } = useQuery<DashboardSummary>({
        queryKey: ['dashboardSummary', dateRange?.from, dateRange?.to],
        queryFn: () => {
            const from = dateRange?.from || ALL_TIME_START;
            const to = dateRange?.to || new Date();
            return dashboardService.getDashboardSummary(from, to);
        },
        enabled: !!dateRange,
    });

    const getDateRangeTitle = () => {
        if (!dateRange?.from) return '';
        const fromStr = format(dateRange.from, 'yyyy-MM-dd');
        const toStr = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : fromStr;
        if (fromStr === format(ALL_TIME_START, 'yyyy-MM-dd')) {
            return 'All Time';
        }
        if (fromStr === toStr) {
            return format(dateRange.from, 'MMM d, yyyy');
        }
        return `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to!, 'MMM d')}`;
    };

    const dateRangeTitle = getDateRangeTitle();
    const periodLabel = dateRangeTitle ? ` (${dateRangeTitle})` : '';

    return (
        <div className="space-y-6 sm:space-y-8">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                        Dashboard
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Business overview{periodLabel}
                    </p>
                </div>

                {/* Date Range Picker */}
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <DateRangePicker
                        date={dateRange}
                        onDateChange={setDateRange}
                    />
                    <Button
                        onClick={() => {
                            queryClient.invalidateQueries({ queryKey: ['dashboardSummary'] });
                        }}
                        variant="outline"
                        size="icon"
                        title="Refresh Dashboard"
                        className="h-9 w-9 sm:h-10 sm:w-10"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Key Metrics Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <DailySalesWidget
                    totalSales={summary?.total_incoming || 0}
                    invoiceCount={summary?.incoming_count || 0}
                    isLoading={summaryLoading}
                    title="Total Incoming"
                    subtitle="Payments received from clients"
                />
                <DailyPaymentsWidget
                    totalPayments={summary?.total_outgoing || 0}
                    paymentCount={summary?.outgoing_count || 0}
                    isLoading={summaryLoading}
                    title="Total Outgoing"
                    subtitle="Payments made to vendors"
                />
                <QuickStatsCard
                    title="Outstanding Receivables"
                    value={formatCurrency(summary?.total_receivables || 0)}
                    description={`${summary?.receivables_count || 0} unpaid invoices`}
                    icon={HandCoins}
                    isLoading={summaryLoading}
                    className="border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-gray-900"
                    iconClassName="bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400"
                />
                <QuickStatsCard
                    title="Outstanding Payables"
                    value={formatCurrency(summary?.total_payables || 0)}
                    description={`${summary?.payables_count || 0} unpaid bills`}
                    icon={Banknote}
                    isLoading={summaryLoading}
                    className="border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-gray-900"
                    iconClassName="bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400"
                />
            </div>

            {/* Tasks Section */}
            <div className="mt-6 sm:mt-8">
                <UpcomingTasksWidget
                    showMyTasksOnly={true}
                    limit={10}
                    showOverdue={true}
                    className="w-full"
                />
            </div>
        </div>
    );
};

export default DashboardPage;
